import React, { Fragment } from 'react';
import { useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Autocomplete } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';

import ComplexQuestionEdit from '../Questions/ComplexQuestion/ComplexQuestionEdit';
import FileUploadEdit from '../Questions/FileUploadQuestion/FileUploadQuestionEdit';
import MatrixQuestionEdit from '../Questions/MatrixQuestion/MatrixQuestionEdit';
import MediaQuestion from '../Questions/MediaQuestion/MediaQuestion';
import MediaQuestionEdit from '../Questions/MediaQuestion/MediaQuestionEdit';
import { RelationalQuestionsEdit } from '../Questions/RelationQuestion/RelationalQuestionEdit';
import SelectionQuestionEdit from '../Questions/SelectionQuestion.jsx/SelectionQuestionEdit';
import TableQuestionEdit from '../Questions/TableQuestion/TableQuestionEdit';

import styles from './EditForm.module.css';

/**
 * Edit form component for create survey page.
 *
 * @param question
 * @param handleInformation
 * @param errorMessage
 * @param helperText
 * @param handleAddOption
 * @param handleInformationOptions
 * @param handleDeleteStars
 * @param handleAddStars
 * @param starMessage
 * @param questionNumber
 * @returns {JSX.Element}
 * @constructor
 */
const EditForm = ({
  question,
  setQuestion,
  questions,
  handleInformation,
  errorMessage,
  helperText,
  handleAddOption,
  handleRemoveOption,
  handleInformationOptions,
  handleDeleteStars,
  handleAddStars,
  starMessage,
  questionNumber,
  categoryError,
  categories,
  handleChangeOptions,
  conditionalQuestion,
  setChildQuestionNumber,
  handleInformationRelationalOptions,
  optionRelationalError,
  customOptionError,
  selectedFileSelectQuestion,
  setSelectedFileSelectQuestion,
  ...props
}) => {

  const [categoryId, setCategoryId] = useState('');
  /**
   * Handle category id change.
   *
   * @param {object} event
   */
  const handleCategoryIdChange = ({ target }) => {
    props.handleCategoryIdChange(target.value);
    setCategoryId(target.value);
  };
  const handleSelect = (uniqueId, { selectedValue, questionNumber, index }) => {
    props.setSelections((prev) => ({
      ...prev,
      [uniqueId]: selectedValue, // Asumiendo que deseas almacenar solo el valor seleccionado
    }));
    if (question.conditionalQuestion) {
      setQuestion((prev) => {
        const newChildQuestionIds = [...prev.childQuestionIds]; // Asume que ya tienes este array en tu estado

        // Asignar el ID de la pregunta hija en la posición correspondiente
        newChildQuestionIds[index] = questionNumber?.toString();

        return {
          ...prev,
          childQuestionIds: newChildQuestionIds, // Actualiza solo el array de IDs de preguntas hijas
        };
      });
    }
  };
  const getFilteredOptions = (uniqueId, questionNumber) => {
    const lastDashIndex = uniqueId.lastIndexOf('-');
    const currentQuestionId = uniqueId.substring(0, lastDashIndex);

    // ✅ Filtrar con validación más estricta
    const filteredQuestions = questions.filter((question) => {
      // Excluir la pregunta actual
      if (question.id === currentQuestionId) return false;

      // Solo preguntas posteriores
      if (question.questionNumber <= questionNumber) return false;

      // ✅ Excluir si tiene CUALQUIER indicador de ser hija
      if (question.complexChildQuestionId) return false;
      if (question.parentId) return false; // ⚠️ Agregar esto si usas parentId

      return true;
    });

    const renumberedQuestions = filteredQuestions.map((q) => {
      const previousNonChildQuestions = questions.filter(
        (prevQ) =>
          !prevQ.complexChildQuestionId &&
          !prevQ.parentId && // ✅ Agregar esto también
          prevQ.questionNumber < q.questionNumber,
      ).length;

      return {
        ...q,
        displayNumber: previousNonChildQuestions + 1,
        originalQuestionNumber: q.questionNumber,
      };
    });

    const preguntaFinal = {
      id: 'cc12a501-cf65-4f2f-bd23-44c79e5c4a64',
      categoryId: 6,
      typeId: 1,
      questionId: null,
      questionOptions: [],
      questionNumber: 0,
      displayNumber: 0,
      type: 'Texto corto',
      name: 'Fin de la encuesta',
      description: '',
      customOptions: undefined,
    };

    renumberedQuestions.push(preguntaFinal);

    return renumberedQuestions;
  };

  const isLastQuestion =
    questions.findIndex((q) => q.id === question.id) === questions.length - 1;

  useEffect(() => {
    const isLast =
      questions.findIndex((q) => q.id === question.id) === questions.length - 1;

    if (question.conditionalQuestion && isLast) {
      setQuestion((prev) => ({
        ...prev,
        conditionalQuestion: false,
      }));
    }
  }, [question.id, question.conditionalQuestion, questions]);

  useEffect(() => {
    if (question.limitType) {
      props.setLimitType(question.limitType); // sincroniza al cargar plantilla
    }
  }, [question.limitType]);

  useEffect(() => {
    if (!question.conditionalQuestion) return;
    if (question.typeId !== 8) return;

    const hasExistingSelections = question.questionOptions?.some(
      (opt) => opt.questionChildren && opt.questionChildren !== '0',
    );
    if (!hasExistingSelections) return;

    props.setSelections((prev) => {
      const updated = { ...prev };

      question.customOptions.forEach((opt, index) => {
        const key = `${question.id}-${index}`;
        if (updated[key]) return;

        const matchingOption = question.questionOptions?.[index];
        const childNumber = matchingOption?.questionChildren;

        if (!childNumber) return;

        let matchedQuestion;

        if (childNumber === '0') {
          // Fin de encuesta — objeto hardcoded igual al de getFilteredOptions
          matchedQuestion = {
            id: 'cc12a501-cf65-4f2f-bd23-44c79e5c4a64',
            categoryId: 6,
            typeId: 1,
            questionId: null,
            questionOptions: [],
            questionNumber: 0,
            displayNumber: 0,
            type: 'Texto corto',
            name: 'Fin de la encuesta',
            description: '',
            customOptions: undefined,
          };
        } else {
          matchedQuestion = questions.find(
            (q) => q.questionNumber?.toString() === childNumber?.toString(),
          );
        }

        if (matchedQuestion) {
          updated[key] = matchedQuestion;
        }
      });

      return updated;
    });
  }, [question.id, question.conditionalQuestion]);

  return (
    <Fragment>
      <div className={styles.form}>
        <div className={styles.top}>
          {/* question name */}
          <div className={styles.question}>
            <div className={styles.input}>
              {question.typeId !== 21 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '4px',
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 'bold',
                      marginRight: '8px',
                    }}
                  >
                    {`Q${questionNumber}`}
                  </p>
                  <p style={{ margin: 0 }}>Pregunta</p>
                </div>
              )}
              <ReactQuill
                theme="snow"
                value={question.name}
                onChange={(value) =>
                  handleInformation({ target: { name: 'name', value } })
                }
                modules={props.quillModules}
                placeholder="Añadir pregunta aquí..."
                style={{
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  marginBottom: '8px',
                }}
              />
              {errorMessage.name && (
                <span style={{ color: '#d32f2f', fontSize: '0.75rem' }}>
                  {helperText.name}
                </span>
              )}
            </div>
          </div>

          {/* question description  */}
          {question.typeId !== 21 && (
            <div className={styles.input}>
              <p>Descripción</p>
              <ReactQuill
                theme="snow"
                value={question.description}
                onChange={(value) =>
                  handleInformation({
                    target: { name: 'description', value },
                  })
                }
                modules={props.quillModules}
                placeholder="Añadir descripción aquí (opcional)..."
                style={{
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  marginTop: '0.5rem',
                  border: '1px solid rgba(0,0,0,0.23)',
                  minHeight: '80px',
                  width: '100%',
                }}
              />
            </div>
          )}

          {/* Solo mostrar opciones adicionales si es tipo 1 */}
          {question.typeId === 1 && (
            <>
              {/* Switch para habilitar respuesta por voz */}
              <div style={{ marginTop: '1rem' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={question.voiceEnabled || false}
                      onChange={(e) =>
                        handleInformation({
                          target: {
                            name: 'voiceEnabled',
                            value: e.target.checked,
                          },
                        })
                      }
                      name="voiceEnabled"
                    />
                  }
                  label="¿Desea grabar voz?"
                />
              </div>

              {/* Si está habilitada la voz, mostrar campo de duración */}
              {question.voiceEnabled && (
                <div className={styles.input} style={{ marginTop: '0.5rem' }}>
                  <TextField
                    id="voice-duration"
                    label="Duración máxima (segundos)"
                    type="number"
                    value={question.voiceDuration || 120}
                    name="voiceDuration"
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      let finalValue = value;
                      if (value > 120) {
                        finalValue = 60;
                      } else if (value < 1 || isNaN(value)) {
                        finalValue = 1;
                      }
                      handleInformation({
                        target: {
                          name: 'voiceDuration',
                          value: finalValue,
                        },
                      });
                    }}
                    inputProps={{
                      min: 1,
                      max: 120,
                    }}
                    fullWidth
                    size="small"
                    helperText="Duración máxima: 60 segundos"
                  />
                </div>
              )}

              {/* Si NO está habilitada la voz, mostrar opciones de validación */}
              {!question.voiceEnabled && (
                <div className={styles.input} style={{ marginTop: '0.5rem' }}>
                  <TextField
                    select
                    label="Tipo de validación"
                    value={question.validationType || 'text'}
                    name="validationType"
                    onChange={handleInformation}
                    fullWidth
                    size="small"
                    helperText="Selecciona el tipo de entrada permitida"
                  >
                    <MenuItem value="text">Alfanumérico (texto libre)</MenuItem>
                    <MenuItem value="number">Solo números</MenuItem>
                    <MenuItem value="email">Solo correo electrónico</MenuItem>
                  </TextField>
                </div>
              )}

              {/* Select para límite de caracteres - SIEMPRE VISIBLE */}
              <div className={styles.input} style={{ marginTop: '0.5rem' }}>
                <TextField
                  select
                  label="Límite de caracteres"
                  value={question.haveLimit ? 'limited' : 'unlimited'}
                  name="haveLimit"
                  onChange={(e) => {
                    const isLimited = e.target.value === 'limited';
                    handleInformation({
                      target: {
                        name: 'haveLimit',
                        value: isLimited,
                      },
                    });
                    // Si cambia a ilimitado, limpiamos el valor del límite
                    if (!isLimited) {
                      handleInformation({
                        target: {
                          name: 'wordRestricted',
                          value: '',
                        },
                      });
                    }
                  }}
                  fullWidth
                  size="small"
                  helperText="¿Deseas limitar la cantidad de caracteres?"
                >
                  <MenuItem value="unlimited">Ilimitado</MenuItem>
                  <MenuItem value="limited">Con límite de caracteres</MenuItem>
                </TextField>
              </div>

              {/* Input para límite de caracteres (solo si eligió "limited") */}
              {question.haveLimit && (
                <div className={styles.input} style={{ marginTop: '0.5rem' }}>
                  <TextField
                    id="character-limit"
                    label="Límite de caracteres"
                    type="number"
                    value={question.wordSizeLimit || ''}
                    name="wordRestricted"
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      handleInformation({
                        target: {
                          name: 'wordSizeLimit',
                          value: value > 0 ? value : '',
                        },
                      });
                    }}
                    inputProps={{
                      min: 8,
                      max: 5000,
                    }}
                    fullWidth
                    size="small"
                    placeholder="Ej: 500"
                    error={errorMessage.wordRestricted}
                    helperText={
                      helperText.wordRestricted ||
                      'Mínimo: 8, Máximo: 5000 caracteres'
                    }
                  />
                </div>
              )}
            </>
          )}

          {/* likert scale */}
          {(question.typeId === 2 || question.typeId === 16) && (
            <div className={styles.options}>
              {question.options.map((val, key) => (
                <div className={styles.option} key={key}>
                  <div
                    style={{
                      backgroundColor: '#fce4e4',
                      borderRadius: '4px',
                      color: '#808080',
                      fontSize: '12px',
                      marginRight: '15px',
                      padding: '3px 9px',
                      textAlign: 'center',
                    }}
                  >
                    {key + 1}
                  </div>
                  <TextField
                    id={`option-${key}`}
                    variant="standard"
                    placeholder="Añadir opción..."
                    value={question.options[key]}
                    onChange={(event) => handleChangeOptions(event, key, true)}
                    InputProps={{
                      disableUnderline: true,
                    }}
                    fullWidth
                    disabled
                    size="small"
                  />
                </div>
              ))}
            </div>
          )}

          {/* multiple option */}
          {/* multiple option */}
          {(question.typeId === 3 || question.typeId === 8) && (
            <>
              <div className={styles.options}>
                {/* Opciones normales */}
                {question.customOptions
                  .map((opt, key) => ({ ...opt, key }))
                  .filter((opt) => !opt.isOther)
                  .map(({ value, key }) => (
                    <>
                      <div key={key} className={styles.option}>
                        {/* Número */}
                        <div
                          style={{
                            backgroundColor: '#F0F2F5',
                            borderRadius: '4px',
                            color: 'rgb(134, 140, 204)',
                            fontSize: '14px',
                            marginRight: '15px',
                            padding: '3px 9px',
                            textAlign: 'center',
                          }}
                        >
                          {key + 1}
                        </div>

                        {/* Campo de texto */}
                        <TextField
                          fullWidth
                          variant="standard"
                          placeholder="Añadir opción..."
                          value={value}
                          onChange={(e) => handleInformationOptions(key)(e)}
                          InputProps={{ disableUnderline: true }}
                          size="small"
                          error={customOptionError[key]}
                          helperText={
                            customOptionError[key]
                              ? 'La opción no puede estar vacía'
                              : ''
                          }
                        />
                      </div>
                      {/* Autocomplete solo si es tipo 8 y no es la última pregunta */}
                      {question.typeId === 8 &&
                        question.conditionalQuestion &&
                        !isLastQuestion && (
                          <Autocomplete
                            disablePortal
                            id={`autocomplete-${question.id}-${key}`}
                            options={getFilteredOptions(
                              `${question.id}-${key}`,
                              question.questionNumber,
                            )}
                            getOptionLabel={(option) => {
                              const cleanName = (option.name || '')
                                .replace(/<[^>]*>/g, '')
                                .trim();
                              return `${option.displayNumber || option.questionNumber}. ${cleanName}`;
                            }}
                            renderOption={(props, option) => {
                              const cleanName = (option.name || '')
                                .replace(/<[^>]*>/g, '')
                                .trim();
                              return (
                                <li {...props}>
                                  {option.displayNumber ||
                                    option.questionNumber}
                                  . {cleanName}
                                </li>
                              );
                            }}
                            value={
                              props.selections[`${question.id}-${key}`] || null
                            }
                            onChange={(event, value) => {
                              // Se guarda el questionNumber real: es con el que el formulario
                              // de respuesta busca la hija. displayNumber no cuenta las
                              // subpreguntas de las complejas y apuntaría a otra pregunta.
                              const dataToSend = {
                                selectedValue: value,
                                questionNumber: value ? value.questionNumber : null,
                                index: key,
                              };
                              handleSelect(`${question.id}-${key}`, dataToSend);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Preguntas"
                                error={
                                  errorMessage.autocomplete &&
                                  !props.selections[`${question.id}-${key}`]
                                }
                                helperText={
                                  errorMessage.autocomplete &&
                                  !props.selections[`${question.id}-${key}`]
                                    ? helperText.autocomplete
                                    : ''
                                }
                              />
                            )}
                          />
                        )}
                    </>
                  ))}

                {/* Opción "Otro" (si existe) SIEMPRE al final */}
                {!question.parentId &&
                  question.customOptions.some((opt) => opt.isOther) &&
                  (() => {
                    const otherIndex = question.customOptions.findIndex(
                      (opt) => opt.isOther,
                    );
                    const otherValue =
                      question.customOptions[otherIndex]?.value || '';
                    const displayIndex =
                      question.customOptions[otherIndex]?.fixedIndex ??
                      question.customOptions.filter((opt) => !opt.isOther)
                        .length;

                    return (
                      <div key="other" className={styles.option}>
                        <div
                          style={{
                            backgroundColor: '#F0F2F5',
                            borderRadius: '4px',
                            color: 'rgb(134, 140, 204)',
                            fontSize: '14px',
                            marginRight: '15px',
                            padding: '3px 9px',
                            textAlign: 'center',
                          }}
                        >
                          {displayIndex + 1}
                        </div>
                        <TextField
                          fullWidth
                          variant="standard"
                          placeholder="Otro (especifique)"
                          value={otherValue}
                          onChange={(e) =>
                            handleInformationOptions(otherIndex)(e)
                          }
                          InputProps={{ disableUnderline: true }}
                          size="small"
                          error={customOptionError[otherIndex]}
                          helperText={
                            customOptionError[otherIndex]
                              ? 'La opción no puede estar vacía'
                              : ''
                          }
                        />
                      </div>
                    );
                  })()}

                {/* Botón: Añadir opción normal */}
                {question.customOptions.length < 20 && (
                  <Button
                    onClick={handleAddOption}
                    startIcon={<AddCircleOutlineIcon />}
                    style={{
                      backgroundColor: '#F7F7F7',
                      width: '255px',
                    }}
                    variant="text"
                  >
                    Añadir opción
                  </Button>
                )}

                {/* Botón: Eliminar última opción normal */}
                {question.customOptions.filter((opt) => !opt.isOther).length >=
                  3 && (
                  <Button
                    variant="text"
                    startIcon={<RemoveCircleOutlineIcon />}
                    onClick={() => {
                      const normalOptions = question.customOptions
                        .map((opt, i) => ({ ...opt, i }))
                        .filter((opt) => !opt.isOther);
                      if (normalOptions.length > 0) {
                        const lastIndex = normalOptions.at(-1)?.i;
                        handleRemoveOption(lastIndex);
                      }
                    }}
                    style={{ backgroundColor: '#F7F7F7', width: '255px' }}
                  >
                    Eliminar opción
                  </Button>
                )}

                {/* Botón: Añadir opción "Otro" */}
                {(question.typeId === 3 ||
                  (question.typeId === 8 && !question.parentId)) && (
                  <>
                    {!question.customOptions.some((opt) => opt.isOther) && (
                      <Button
                        variant="text"
                        onClick={() =>
                          props.updateQuestion(question.id, {
                            customOptions: [
                              ...question.customOptions,
                              {
                                value: '',
                                isOther: true,
                                fixedIndex: question.customOptions.filter(
                                  (o) => !o.isOther,
                                ).length, // 👈 guarda posición
                              },
                            ],
                          })
                        }
                        style={{
                          backgroundColor: '#F7F7F7',
                          width: '255px',
                          marginTop: '0.5rem',
                        }}
                      >
                        Añadir opción "Otro"
                      </Button>
                    )}

                    {/* Botón: Eliminar opción "Otro" */}
                    {question.customOptions.some((opt) => opt.isOther) && (
                      <Button
                        variant="text"
                        color="error"
                        onClick={() =>
                          props.updateQuestion(question.id, {
                            customOptions: question.customOptions.filter(
                              (opt) => !opt.isOther,
                            ),
                          })
                        }
                        style={{
                          backgroundColor: '#FDECEA',
                          width: '255px',
                          marginTop: '0.5rem',
                        }}
                      >
                        Eliminar opción "Otro"
                      </Button>
                    )}
                  </>
                )}
              </div>

              {/* Tipo 3: Límite de selección */}
              {question.typeId === 3 && (
                <div className={styles.input}>
                  <FormControl
                    fullWidth
                    size="small"
                    style={{ marginTop: '1rem' }}
                  >
                    <InputLabel id="limit-type-label">
                      Tipo de límite
                    </InputLabel>
                    <Select
                      labelId="limit-type-label"
                      value={props.limitType}
                      label="Tipo de límite"
                      onChange={(event) => {
                        const newValue = event.target.value;
                        props.setLimitType(newValue);
                        setQuestion((prev) => ({
                          ...prev,
                          limitType: newValue,
                          stars: newValue === 'ilimitado' ? [] : prev.stars,
                        }));
                      }}
                    >
                      <MenuItem value="ilimitado">Ilimitado</MenuItem>
                      <MenuItem value="fijo">Valor fijo</MenuItem>
                    </Select>
                  </FormControl>

                  {props.limitType === 'fijo' && (
                    <TextField
                      label="Valor fijo"
                      placeholder="Por favor ingresa el valor máximo que los usuarios pueden seleccionar:"
                      value={question.stars}
                      onChange={handleInformation}
                      style={{ marginTop: '0.8rem' }}
                      fullWidth
                      size="small"
                      name="stars"
                      error={errorMessage.maximunValueOptions}
                      helperText={helperText.maximunValueOptions}
                    />
                  )}
                </div>
              )}
            </>
          )}

          {/* ratings */}
          {(question.type === 'Calificaciones' || question.typeId === 5) && (
            <Fragment>
              <div className={styles.stars}>
                <Button
                  onClick={handleDeleteStars}
                  style={{
                    backgroundColor: '#F7F7F7',
                    color: 'black',
                    fontSize: '1.8rem',
                    padding: 0,
                  }}
                  variant="text"
                >
                  -
                </Button>
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '18px 20px',
                  }}
                >
                  {Array.isArray(question.stars)
                    ? question.stars.length
                    : typeof question.stars === 'number'
                      ? question.stars
                      : 0}
                </div>
                <Button
                  onClick={handleAddStars}
                  style={{
                    backgroundColor: '#F7F7F7',
                    color: 'black',
                    fontSize: '1.8rem',
                    padding: 0,
                  }}
                  variant="text"
                >
                  +
                </Button>
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    marginTop: '0.3em',
                  }}
                >
                  {(Array.isArray(question.stars)
                    ? question.stars
                    : Array.from({
                        length:
                          typeof question.stars === 'number'
                            ? question.stars
                            : 0,
                      })
                  ).map((val, index) => (
                    <svg
                      fill="none"
                      height="50"
                      key={index}
                      viewBox="0 0 20 20"
                      width="50"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.978 15.544L8.00001 11.8854L3.02201 15.544L4.93334 9.63536L-0.0419922 6.00003H6.10067L8.00001 0.0813599L9.89934 6.00003H16.0413L11.0667 9.63536L12.978 15.544Z"
                        fill="#ddd"
                      ></path>
                    </svg>
                  ))}
                </div>
              </div>
              <sub>{starMessage}</sub>
            </Fragment>
          )}

          {/* ratings */}
          {(question.type === 'Relacional' || question.typeId == 15) && (
            <Fragment>
              <RelationalQuestionsEdit
                question={question}
                setQuestion={setQuestion}
                handleInformationOptions={handleInformationOptions}
                handleInformationRelationalOptions={
                  handleInformationRelationalOptions
                }
                optionRelationalError={optionRelationalError}
                customOptionError={customOptionError}
                type={question.typeId}
                handleaddoption={handleAddOption}
              />
            </Fragment>
          )}

          {/* ratings */}
          {question.typeId === 19 && (
            <Fragment>
              <div className={styles.top}>
                {/* Input numérico para el valor */}
                <div
                  style={{
                    marginTop: '4px',
                  }}
                >
                  <TextField
                    label="Valor de respuesta"
                    type="number"
                    inputProps={{ min: 0, max: 10 }}
                    value={question.stars}
                    name="stars"
                    onChange={handleInformation}
                    variant="standard"
                    fullWidth
                    error={errorMessage.bipolar}
                    helperText={errorMessage.bipolar ? helperText.bipolar : ''}
                  />
                </div>
                {/* Textos para los extremos de la escala */}
                <div
                  style={{
                    marginTop: '4px',
                  }}
                >
                  <TextField
                    label="Texto extremo izquierdo"
                    placeholder="Ej: Totalmente en desacuerdo"
                    value={question.textsBipolarBar.leftText}
                    name="textsBipolarBar.leftText"
                    onChange={handleInformation}
                    size="small"
                    variant="standard"
                    error={errorMessage.bipolarText}
                    fullWidth
                    inputProps={{
                      maxLength: 300, // ✅ Limita la entrada a 300 caracteres
                    }}
                    helperText={
                      errorMessage.bipolarText
                        ? helperText.bipolarText
                        : `${question.textsBipolarBar.leftText.length}/300` // ✅ Muestra el contador
                    }
                  />
                </div>
                <div
                  style={{
                    marginTop: '4px',
                  }}
                >
                  <TextField
                    label="Texto extremo derecho"
                    placeholder="Ej: Totalmente de acuerdo"
                    value={question.textsBipolarBar.rightText}
                    name="textsBipolarBar.rightText"
                    onChange={handleInformation}
                    size="small"
                    variant="standard"
                    error={errorMessage.bipolarText}
                    fullWidth
                    inputProps={{
                      maxLength: 300, // ✅ Limita la entrada a 300 caracteres
                    }}
                    helperText={
                      errorMessage.bipolarText
                        ? helperText.bipolarText
                        : `${question.textsBipolarBar.rightText.length}/300` // ✅ Muestra el contador
                    }
                  />
                </div>
              </div>
            </Fragment>
          )}

          {/* Escala Opinion*/}
          {question.typeId === 22 && (
            <div className={styles.top}>
              {/* Textos para los extremos de la escala */}
              <div
                style={{
                  marginTop: '4px',
                }}
              >
                <TextField
                  label="Texto extremo izquierdo"
                  placeholder="Ej: Totalmente en desacuerdo"
                  value={question.textsBipolarBar.leftText}
                  name="textsBipolarBar.leftText"
                  onChange={handleInformation}
                  size="small"
                  variant="standard"
                  style={{ width: '45%' }}
                  error={errorMessage.bipolarText}
                  inputProps={{
                    maxLength: 25, // ✅ Limita la entrada a 120 caracteres
                  }}
                  helperText={
                    errorMessage.bipolarText
                      ? helperText.bipolarText
                      : `${question.textsBipolarBar.leftText.length}/25` // ✅ Muestra el contador
                  }
                />
              </div>
              <div
                style={{
                  marginTop: '4px',
                }}
              >
                <TextField
                  label="Texto extremo derecho"
                  placeholder="Ej: Totalmente de acuerdo"
                  value={question.textsBipolarBar.rightText}
                  name="textsBipolarBar.rightText"
                  onChange={handleInformation}
                  size="small"
                  variant="standard"
                  style={{ width: '45%' }}
                  error={errorMessage.bipolarText}
                  inputProps={{
                    maxLength: 25, // ✅ Limita la entrada a 120 caracteres
                  }}
                  helperText={
                    errorMessage.bipolarText
                      ? helperText.bipolarText
                      : `${question.textsBipolarBar.rightText.length}/25` // ✅ Muestra el contador
                  }
                />
              </div>
              <div className={styles.input} style={{ marginTop: '1rem' }}>
                <FormControl
                  fullWidth
                  size="small"
                  style={{ marginBottom: '1rem' }}
                >
                  <InputLabel id="first-select-label">
                    Seleccionar 0 o 1
                  </InputLabel>
                  <Select
                    labelId="first-select-label"
                    value={
                      [0, 1].includes(
                        Number(question.textsBipolarBar.valueLeft),
                      )
                        ? question.textsBipolarBar.valueLeft
                        : '0'
                    }
                    label="Seleccionar 0 o 1"
                    onChange={handleInformation}
                    name="textsBipolarBar.valueLeft"
                  >
                    <MenuItem value={'0'}>0</MenuItem>
                    <MenuItem value={'1'}>1</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel id="second-select-label">
                    Seleccionar un número
                  </InputLabel>
                  <Select
                    labelId="second-select-label"
                    value={
                      question.secondSelectOptions.includes(
                        question.textsBipolarBar.valueRight,
                      )
                        ? question.textsBipolarBar.valueRight
                        : question.secondSelectOptions.length > 0
                          ? question.secondSelectOptions[0].toString()
                          : ''
                    }
                    label="Seleccionar un número"
                    onChange={handleInformation}
                    name="textsBipolarBar.valueRight"
                  >
                    {question.secondSelectOptions.map((num) => (
                      <MenuItem key={num} value={num}>
                        {num}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          )}

          {question.typeId === 24 && (
            <>
              <div className={styles.top}>
                <FormControl component="fieldset" sx={{ mt: 2, mb: 4 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={question.autoValidate}
                        onChange={handleInformation}
                        name="autoValidate"
                      />
                    }
                    label="¿Deseas que el valor de la escala se valide automáticamente?"
                  />
                  <FormHelperText>
                    Si se activa, el sistema validará automaticamente que el
                    valor esté dentro del rango esperado.
                  </FormHelperText>
                </FormControl>

                <div
                  style={{
                    marginTop: '4px',
                  }}
                >
                  <TextField
                    label="Valor de la escala"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={question.stars}
                    name="stars"
                    onChange={handleInformation}
                    variant="standard"
                    fullWidth
                    error={errorMessage.bipolar}
                    helperText={errorMessage.bipolar ? helperText.bipolar : ''}
                  />
                </div>
                {question.customOptions.map((val, key) => {
                  return (
                    <div
                      className={styles.option}
                      key={key}
                      style={{
                        marginTop: '10px',
                      }}
                    >
                      <div
                        style={{
                          padding: '3px 9px',
                          backgroundColor: '#F0F2F5',
                          borderRadius: '4px',
                          textAlign: 'center',
                          marginRight: '15px',
                          fontSize: '14px',
                          color: 'rgb(134, 140, 204)',
                        }}
                      >
                        {key + 1}
                      </div>
                      <TextField
                        id="outlined-name"
                        variant="standard"
                        placeholder="Añadir opción..."
                        value={question.customOptions[key].value}
                        onChange={handleInformationOptions(key)}
                        error={customOptionError[key]}
                        helperText={
                          customOptionError[key]
                            ? 'La opción no puede estar vacía'
                            : ''
                        }
                        InputProps={{
                          disableUnderline: true,
                        }}
                        fullWidth
                        size="small"
                      />
                    </div>
                  );
                })}
              </div>
              {question.customOptions.length < 20 ? (
                <Button
                  variant="text"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleAddOption}
                  style={{ backgroundColor: '#F7F7F7', width: '255px' }}
                >
                  Añadir opción
                </Button>
              ) : null}
              {question.customOptions.length >= 3 ? (
                <Button
                  variant="text"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() =>
                    handleRemoveOption(question.customOptions.length - 1)
                  }
                  style={{ backgroundColor: '#F7F7F7', width: '255px' }}
                >
                  Eliminar opción
                </Button>
              ) : null}
            </>
          )}

          {question.typeId === 25 && (
            <>
              <MatrixQuestionEdit
                question={question}
                setQuestion={setQuestion}
                handleInformationOptions={handleInformationOptions}
                handleInformationRelationalOptions={
                  handleInformationRelationalOptions
                }
                optionRelationalError={optionRelationalError}
                errorMessage={errorMessage}
                helperText={helperText}
                type={question.typeId}
                handleaddoption={handleAddOption}
              />
            </>
          )}
          {question.typeId === 26 && (
            <>
              <ComplexQuestionEdit
                question={question}
                setQuestion={setQuestion}
                handleInformationOptions={handleInformationOptions}
                handleInformationRelationalOptions={
                  handleInformationRelationalOptions
                }
                optionRelationalError={optionRelationalError}
                errorMessage={errorMessage}
                helperText={helperText}
                setErrorMessage={props.setErrorMessage}
                setHelperText={props.setHelperText}
                type={question.typeId}
                handleaddoption={handleAddOption}
                selectedFileSelectQuestion={selectedFileSelectQuestion}
                setSelectedFileSelectQuestion={setSelectedFileSelectQuestion}
                setIsUploadingFile={props.setIsUploadingFile}
              />
            </>
          )}
          {question.typeId === 27 && (
            <>
              <SelectionQuestionEdit
                question={question}
                setQuestion={setQuestion}
                handleInformationOptions={handleInformationOptions}
                handleInformationRelationalOptions={
                  handleInformationRelationalOptions
                }
                optionRelationalError={optionRelationalError}
                errorMessage={errorMessage}
                helperText={helperText}
                setErrorMessage={props.setErrorMessage}
                setHelperText={props.setHelperText}
                type={question.typeId}
                handleaddoption={handleAddOption}
                selectedFileSelectQuestion={selectedFileSelectQuestion}
                setSelectedFileSelectQuestion={setSelectedFileSelectQuestion}
                setIsUploadingFile={props.setIsUploadingFile}
              />
            </>
          )}
          {question.typeId === 28 && (
            <>
              <MediaQuestionEdit
                question={question}
                setQuestion={setQuestion}
                handleInformation={handleInformation}
                errorMessage={errorMessage}
                helperText={helperText}
                setErrorMessage={props.setErrorMessage}
                setHelperText={props.setHelperText}
                type={question.typeId}
                handleaddoption={handleAddOption}
                selectedFileSelectQuestion={selectedFileSelectQuestion}
                setSelectedFileSelectQuestion={setSelectedFileSelectQuestion}
                setIsUploadingFile={props.setIsUploadingFile}
              />
            </>
          )}
          {question.typeId === 29 && (
            <>
              <TableQuestionEdit
                question={question}
                setQuestion={setQuestion}
                errorMessage={errorMessage}
                helperText={helperText}
              />
            </>
          )}
          {question.typeId === 30 && (
            <>
              <FileUploadEdit
                question={question}
                setQuestion={setQuestion}
                errorMessage={errorMessage}
                helperText={helperText}
              />
            </>
          )}
        </div>
      </div>
      {(question.typeId && question.typeId) !== 21 && (
        <Box
          sx={{
            mt: 4,
          }}
        >
          <FormControl fullWidth error={categoryError.length > 0}>
            <InputLabel
              id="category-id-label"
              sx={{
                backgroundColor: 'white',
                paddingRight: '0 6px',
              }}
            >
              Seleccionar categoría
            </InputLabel>
            <Select
              labelId="category-id-label"
              id="category-id"
              label="Categoría"
              value={categoryId || question.categoryId}
              onChange={handleCategoryIdChange}
            >
              <MenuItem value={question.categoryId}>
                <em>Seleccione</em>
              </MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.nameCatogory}
                </MenuItem>
              ))}
            </Select>
            {categoryError.length > 0 && (
              <FormHelperText>{categoryError}</FormHelperText>
            )}
          </FormControl>
        </Box>
      )}
    </Fragment>
  );
};

EditForm.propTypes = {
  question: PropTypes.object.isRequired,
  handleInformation: PropTypes.func.isRequired,
  errorMessage: PropTypes.object.isRequired,
  helperText: PropTypes.object.isRequired,
  handleAddOption: PropTypes.func.isRequired,
  handleInformationOptions: PropTypes.func.isRequired,
  handleDeleteStars: PropTypes.func.isRequired,
  handleAddStars: PropTypes.func.isRequired,
  starMessage: PropTypes.string.isRequired,
  questionNumber: PropTypes.number.isRequired,
  handleChangeOptions: PropTypes.func,
};

EditForm.defaultProps = {
  question: {},
  questionNumber: 1,
  handleInformation: () => {},
};

export default EditForm;
